import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Chip,
  Divider,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link
} from "@mui/material";
import {
  Work,
  LocationOn,
  AccessTime,
  Business,
  Favorite,
  School,
  EmojiEvents,
  TrendingUp,
  ArrowForward,
  Send
} from "@mui/icons-material";

const Careers = () => {
  const theme = useTheme();

  const jobOpenings = [
    {
      title: "Senior Full‑Stack Engineer",
      department: "Engineering",
      location: "San Francisco, CA (Hybrid)",
      type: "Full‑time",
      description: "Build and scale our booking platform, payment systems, and real‑time messaging features."
    },
    {
      title: "Product Manager – Marketplace",
      department: "Product",
      location: "Remote (US)",
      type: "Full‑time",
      description: "Drive the roadmap for both host and cleaner experiences, improving matching and retention."
    },
    {
      title: "Customer Success Specialist",
      department: "Operations",
      location: "San Francisco, CA",
      type: "Full‑time",
      description: "Help hosts and cleaners resolve issues and provide feedback to improve our platform."
    },
    {
      title: "Cleaner Onboarding Coordinator",
      department: "Operations",
      location: "Remote (US)",
      type: "Full‑time",
      description: "Manage the verification and training process for new cleaners joining FreshSweeper."
    },
    {
      title: "Marketing Manager – Growth",
      department: "Marketing",
      location: "San Francisco, CA (Hybrid)",
      type: "Full‑time",
      description: "Lead user acquisition campaigns for both sides of the marketplace."
    },
    {
      title: "Mobile Engineer (React Native)",
      department: "Engineering",
      location: "Remote (US)",
      type: "Full‑time",
      description: "Develop and maintain our iOS and Android apps used by thousands of cleaners and hosts."
    }
  ];

  const benefits = [
    { icon: <Favorite />, title: "Health & Wellness", description: "Comprehensive medical, dental, and vision coverage" },
    { icon: <School />, title: "Learning Stipend", description: "$2,000/year for courses, conferences, and books" },
    { icon: <EmojiEvents />, title: "Equity Package", description: "Competitive stock options so you share in our success" },
    { icon: <TrendingUp />, title: "401(k) Matching", description: "Up to 4% match on retirement contributions" },
    { icon: <AccessTime />, title: "Flexible PTO", description: "Unlimited paid time off with a minimum 3 weeks encouraged" },
    { icon: <Work />, title: "Remote‑First", description: "Work from home or one of our office hubs" }
  ];

  const values = [
    "Trust & Transparency",
    "Quality First",
    "Empower Cleaners",
    "Delight Hosts",
    "Continuous Learning",
    "Act Like an Owner"
  ];

  const handleApply = (jobTitle) => {
    // In a real implementation, open application form or redirect to careers@freshsweeper.com
    window.location.href = `mailto:careers@freshsweeper.com?subject=Application for ${jobTitle}`;
  };

  const sections = [
    { id: "culture", title: "Our Culture" },
    { id: "benefits", title: "Benefits" },
    { id: "openings", title: "Open Positions" }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Chip
          label="Join Us"
          sx={{
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2rem", md: "3rem" }
          }}
        >
          Build the Future of Cleaning
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Join a passionate team working to empower cleaners and delight hosts. 
          We’re building the most trusted platform in the cleaning industry.
        </Typography>
      </Box>

      {/* Hero stat bar */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 3
            }}
          >
            <Typography variant="h3" fontWeight={700} color="primary">10k+</Typography>
            <Typography variant="body2" color="text.secondary">Cleaners on platform</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 3
            }}
          >
            <Typography variant="h3" fontWeight={700} color="primary">250k+</Typography>
            <Typography variant="body2" color="text.secondary">Jobs completed</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              borderRadius: 3
            }}
          >
            <Typography variant="h3" fontWeight={700} color="primary">4.98⭐</Typography>
            <Typography variant="body2" color="text.secondary">Average rating</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 100,
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.8),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              On this page
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, listStyle: "none" }}>
              {sections.map((section) => (
                <Box component="li" key={section.id} sx={{ mb: 0.5 }}>
                  <Link
                    href={`#${section.id}`}
                    underline="hover"
                    sx={{
                      fontSize: "0.85rem",
                      color: theme.palette.text.secondary,
                      "&:hover": { color: theme.palette.primary.main },
                      textDecoration: "none"
                    }}
                  >
                    {section.title}
                  </Link>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Main content */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 4 },
              borderRadius: 3,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            {/* Culture */}
            <Box id="culture" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Our Culture
              </Typography>
              <Typography variant="body1" paragraph>
                At FreshSweeper, we believe that a great workplace is built on trust, empathy, 
                and a shared mission. We’re a remote‑first team with a hub in San Francisco, 
                and we value diversity, continuous learning, and acting like owners.
              </Typography>
              <Typography variant="body1" paragraph>
                Whether you’re writing code, supporting cleaners, or shaping our product strategy, 
                your work directly impacts thousands of small business owners (cleaners) and 
                homeowners (hosts). We move fast, embrace feedback, and celebrate wins together.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                {values.map((value, idx) => (
                  <Chip key={idx} label={value} variant="outlined" />
                ))}
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Benefits */}
            <Box id="benefits" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Benefits & Perks
              </Typography>
              <Grid container spacing={2}>
                {benefits.map((benefit, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                        {benefit.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{benefit.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{benefit.description}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Open Positions */}
            <Box id="openings" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Open Positions
              </Typography>
              <Typography variant="body1" paragraph>
                Join us in making cleaning services more reliable and empowering cleaners. 
                See our current openings below.
              </Typography>
              <Grid container spacing={2}>
                {jobOpenings.map((job, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Card sx={{ borderRadius: 3 }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight={700}>{job.title}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1, mb: 1 }}>
                          <Chip icon={<Business />} label={job.department} size="small" variant="outlined" />
                          <Chip icon={<LocationOn />} label={job.location} size="small" variant="outlined" />
                          <Chip icon={<AccessTime />} label={job.type} size="small" variant="outlined" />
                        </Box>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {job.description}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ px: 2, pb: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<Send />}
                          onClick={() => handleApply(job.title)}
                        >
                          Apply Now
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 4, p: 3, textAlign: 'center', backgroundColor: alpha(theme.palette.primary.main, 0.03), borderRadius: 3 }}>
                <Typography variant="body1" gutterBottom>
                  Don’t see a role that fits? We’re always looking for great people.
                </Typography>
                <Button variant="outlined" startIcon={<Send />} href="mailto:careers@freshsweeper.com">
                  Send your resume
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Careers;